import { Check, Cloud, GripVertical, Monitor, Server, Smartphone, Tablet } from 'lucide-react';

/**
 * One small working picture per feature, so each reads without being opened.
 * They loop on CSS keyframes (prefixed `ft-`, defined once in `FeatureStyles`)
 * and only under `prefers-reduced-motion: no-preference`; otherwise each rests
 * on a sensible still frame.
 */

const LINE = 'border-secondary-dark/[0.07]';
const FRAME = `relative h-[212px] overflow-hidden rounded-xl border bg-[#f5f8fc] p-4 ${LINE}`;

function Caption({ children }) {
  return (
    <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-secondary-dark/45">
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Designed for the digital era — a workflow the lab arranges itself  */
/* ------------------------------------------------------------------ */

const STEPS = [
  { name: 'Surfacing', on: true },
  { name: 'AR coating', on: true, move: 'ft-drop' },
  { name: 'Tinting', on: false, move: 'ft-rise' },
  { name: 'Edging', on: true },
];

export function WorkflowVisual() {
  return (
    <div className={FRAME}>
      <Caption>Your workflow · customised</Caption>
      <div className="mt-3 space-y-1.5">
        {STEPS.map((s) => (
          <div
            key={s.name}
            className={`flex h-[30px] items-center gap-2 rounded-lg border bg-white px-2 ${LINE} ${s.move ?? ''} ${s.move === 'ft-drop' ? 'relative z-10 shadow-[0_8px_20px_-8px_rgba(1,167,255,0.5)]' : ''}`}
          >
            <GripVertical className="size-3.5 text-secondary-dark/25" strokeWidth={2} />
            <span className="flex-1 text-xs font-medium text-secondary-dark">{s.name}</span>
            <span
              className={`relative h-4 w-7 rounded-full ${s.on ? 'bg-primary-blue' : 'bg-secondary-dark/15'}`}
            >
              <span
                className={`absolute top-0.5 size-3 rounded-full bg-white ${s.on ? 'right-0.5' : 'left-0.5'}`}
              />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Server based or cloud — the same system, either way                */
/* ------------------------------------------------------------------ */

export function DeployVisual() {
  return (
    <div className={FRAME}>
      <Caption>Deployment</Caption>

      <div className="relative mt-3 grid grid-cols-2 rounded-lg bg-white p-1 text-xs font-semibold">
        <span className="ft-thumb absolute inset-y-1 left-1 w-[calc(50%-4px)] rounded-md bg-ink" />
        <span className="relative py-1.5 text-center">
          <span className="ft-a text-white">Your server</span>
          <span className="ft-b absolute inset-0 grid place-items-center text-secondary-dark/50 opacity-0">
            Your server
          </span>
        </span>
        <span className="relative py-1.5 text-center">
          <span className="ft-a text-secondary-dark/50">Cloud</span>
          <span className="ft-b absolute inset-0 grid place-items-center text-white opacity-0">
            Cloud
          </span>
        </span>
      </div>

      <div className="relative mx-auto mt-3 grid size-10 place-items-center rounded-xl bg-primary-blue text-white">
        <Server className="ft-a size-5" strokeWidth={1.75} />
        <Cloud className="ft-b absolute size-5 opacity-0" strokeWidth={1.75} />
      </div>

      <svg viewBox="0 0 240 40" fill="none" className="mx-auto block h-10 w-full max-w-[260px]">
        {['M120 0C120 26 40 14 40 40', 'M120 0V40', 'M120 0C120 26 200 14 200 40'].map((d) => (
          <path
            key={d}
            d={d}
            stroke="#01a7ff"
            strokeWidth="1.5"
            strokeDasharray="4 6"
            strokeLinecap="round"
            className="motion-safe:animate-dash"
          />
        ))}
      </svg>

      <div className="mx-auto grid max-w-[260px] grid-cols-3 text-secondary-dark/55">
        {[Monitor, Tablet, Smartphone].map((Icon, i) => (
          <span key={i} className="grid place-items-center">
            <span className={`grid size-8 place-items-center rounded-lg border bg-white ${LINE}`}>
              <Icon className="size-4" strokeWidth={1.75} />
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Simplified pricing — pay for the modules you use                   */
/* ------------------------------------------------------------------ */

const MODULES = ['Remote Order Entry', 'Lab Management', 'Practice Management', 'Edging Module'];

export function PricingVisual() {
  return (
    <div className={FRAME}>
      <Caption>Your plan</Caption>
      <div className="mt-3 space-y-1.5">
        {MODULES.map((m, i) => {
          const last = i === MODULES.length - 1;
          return (
            <div
              key={m}
              className={`flex h-[26px] items-center gap-2 rounded-lg border bg-white px-2 ${LINE}`}
            >
              <span className="relative grid size-4 place-items-center rounded-[5px] border border-secondary-dark/20">
                <span
                  className={`absolute -inset-px grid place-items-center rounded-[5px] bg-primary-blue text-white ${last ? 'ft-tick opacity-0' : ''}`}
                >
                  <Check className="size-3" strokeWidth={3.5} />
                </span>
              </span>
              <span className="text-xs font-medium text-secondary-dark">{m}</span>
            </div>
          );
        })}
      </div>
      <div className="mt-2.5 flex items-center justify-between border-t border-dashed border-secondary-dark/15 pt-2.5 font-mono text-[10px] uppercase tracking-[0.08em] text-secondary-dark/55">
        <span className="relative">
          <span className="ft-a">3 modules</span>
          <span className="ft-b absolute left-0 top-0 whitespace-nowrap text-primary-blue opacity-0">
            4 modules
          </span>
        </span>
        <span>Hidden fees · 0</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Keyframes, rendered once by the section                            */
/* ------------------------------------------------------------------ */

export function FeatureStyles() {
  return (
    <style>{`
      @media (prefers-reduced-motion: no-preference) {
        .ft-drop { animation: ft-drop 6s cubic-bezier(0.65, 0, 0.35, 1) infinite; }
        .ft-rise { animation: ft-rise 6s cubic-bezier(0.65, 0, 0.35, 1) infinite; }
        .ft-thumb { animation: ft-thumb 6s cubic-bezier(0.65, 0, 0.35, 1) infinite; }
        .ft-a { animation: ft-a 6s linear infinite; }
        .ft-b { animation: ft-b 6s linear infinite; }
        .ft-tick { animation: ft-b 6s linear infinite; }
      }
      @keyframes ft-drop {
        0%, 30% { transform: translateY(0); }
        42%, 82% { transform: translateY(36px); }
        94%, 100% { transform: translateY(0); }
      }
      @keyframes ft-rise {
        0%, 30% { transform: translateY(0); }
        42%, 82% { transform: translateY(-36px); }
        94%, 100% { transform: translateY(0); }
      }
      @keyframes ft-thumb {
        0%, 38% { transform: translateX(0); }
        50%, 88% { transform: translateX(calc(100% + 0px)); }
        100% { transform: translateX(0); }
      }
      @keyframes ft-a {
        0%, 40% { opacity: 1; }
        48%, 90% { opacity: 0; }
        98%, 100% { opacity: 1; }
      }
      @keyframes ft-b {
        0%, 40% { opacity: 0; }
        48%, 90% { opacity: 1; }
        98%, 100% { opacity: 0; }
      }
    `}</style>
  );
}
